import { LoadingIndicator } from 'components/LoadingIndicator';
import { useChain, usePrice, useSettings } from 'contexts';
import React, { useEffect, useMemo, useState } from 'react';
import { formatCount } from 'utils/number';
import { ChainId, Currency } from 'utils/types';
import Card from './Card';
import type { CardProps } from './types';

type Props = CardProps & {
  chainId?: ChainId;
  amount?: number;
  baseCurrency?: Currency;
  defaultQuoteCurrency?: Currency;
  hideSurfOption?: boolean;
};

const renderCurrencySymbol = (currency: Currency, dark = false): React.ReactNode | undefined => {
  switch (currency) {
    case Currency.USD:
      return '$';
    case Currency.BNB:
      return (
        <img
          src={dark ? '/img/chains-outlined/bsc-light.svg' : '/img/chains-outlined/bsc.svg'}
          alt={currency}
          className="w-12 h-12 -mt-1 mr-2"
        />
      );
    case Currency.ETH:
      return (
        <img
          src={dark ? '/img/chains-outlined/eth-light.svg' : '/img/chains-outlined/eth.svg'}
          alt={currency}
          className="w-12 h-12 -mt-1 mr-2"
        />
      );
    case Currency.MATIC:
      return (
        <img
          src={dark ? '/img/chains-outlined/polygon-light.svg' : '/img/chains-outlined/polygon.svg'}
          alt={currency}
          className="w-12 h-12 -mt-1 mr-2"
        />
      );
    case Currency.SURF:
      return (
        <img src="/img/chains-outlined/surf.svg" alt={currency} className="w-12 h-12 -mt-1 mr-2" />
      );
    case Currency.WAVE:
      return <img src="/img/tokens/wave.png" alt={currency} className="w-12 h-12 -mt-1 mr-2" />;
    default:
      return undefined;
  }
};

const CurrencyToggleCard: React.FC<Props> = ({
  chainId,
  amount,
  baseCurrency = Currency.SURF,
  defaultQuoteCurrency,
  hideSurfOption = false,
  filled = false,
  ...cardProps
}) => {
  const normalizedDefaultQuoteCurrency = useMemo(() => {
    if (defaultQuoteCurrency) {
      return defaultQuoteCurrency;
    }

    switch (chainId) {
      case ChainId.XDai:
        return Currency.USD;
      case ChainId.BscMainnet:
        return Currency.BNB;
      default:
        return Currency.ETH;
    }
  }, [defaultQuoteCurrency, chainId]);
  const [quoteCurrency, setQuoteCurrency] = useState(normalizedDefaultQuoteCurrency);
  const { getChainTokenPrices } = usePrice();
  const { styleSlug } = useChain();
  const { isDarkMode } = useSettings();

  const tokenPrices = useMemo(() => getChainTokenPrices(chainId), [chainId, getChainTokenPrices]);

  const displayValue = useMemo(() => {
    const rate = tokenPrices[baseCurrency]?.[quoteCurrency];
    return typeof rate !== 'undefined' && typeof amount !== 'undefined'
      ? formatCount(rate * amount)
      : undefined;
  }, [tokenPrices, amount, baseCurrency, quoteCurrency]);

  useEffect(() => {
    setQuoteCurrency(normalizedDefaultQuoteCurrency);
  }, [normalizedDefaultQuoteCurrency]);

  const quoteCurrencyOptions = tokenPrices[baseCurrency]
    ? Object.keys(tokenPrices[baseCurrency]!)
        .filter(
          (currency) =>
            (!hideSurfOption || currency !== Currency.SURF) &&
            (amount !== 1 || currency !== baseCurrency)
        )
        .sort()
    : [];

  return (
    <Card
      {...cardProps}
      filled={filled}
      body={
        <div className="flex items-center justify-center">
          {typeof displayValue !== 'undefined' ? (
            <>
              {renderCurrencySymbol(quoteCurrency, filled || isDarkMode)}
              {displayValue}
            </>
          ) : (
            <LoadingIndicator />
          )}
        </div>
      }
      footer={
        <select
          className={`border border-${styleSlug}-light dark:border-${styleSlug}-dark bg-white dark:bg-gray-111 rounded-md py-1 text-lg text-center min-w-1/4`}
          style={{ textAlignLast: 'center' }}
          value={quoteCurrency}
          onChange={(e) => setQuoteCurrency(e.target.value as Currency)}
        >
          {quoteCurrencyOptions.map((currency) => (
            <option key={currency} value={currency}>
              {currency}
            </option>
          ))}
        </select>
      }
    ></Card>
  );
};

export default CurrencyToggleCard;
